/**
 * Timezone helpers shared by web, mobile, and (in spirit) the Cloud Functions.
 *
 * Two timezones matter in this product and they are NOT the same thing:
 *
 *   users/{uid}.timezone            — where THIS parent actually is. Stamped
 *                                     daily by the session ping, so it follows
 *                                     someone who moves or relocates.
 *   families/{id}.settings.timezone — the household's nominal timezone. Set at
 *                                     family creation from whichever device
 *                                     created it, and editable in Settings.
 *
 * The nightly handoff reminder prefers the per-user value and falls back to the
 * family's. That ordering matters for long-distance co-parenting: two parents
 * in different zones should each get "you have the kids tomorrow" at 6pm THEIR
 * time, not at 6pm wherever the family record happened to be created.
 */

/**
 * The device's IANA timezone, e.g. 'America/New_York'.
 * Returns null when the platform can't tell us rather than guessing UTC —
 * callers decide what an unknown timezone should fall back to.
 */
export function getDeviceTimezone() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return isValidTimezone(tz) ? tz : null;
  } catch {
    return null;
  }
}

/**
 * Is this a timezone the runtime can actually format with?
 *
 * Checked by attempting a format rather than matching a list: the set of valid
 * IANA zones differs between Node, browsers, and Hermes, and a value that one
 * accepts can throw a RangeError on another. A stored timezone that throws
 * would break the settings screen for that family.
 */
export function isValidTimezone(tz) {
  if (!tz || typeof tz !== 'string') return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz }).format(new Date());
    return true;
  } catch {
    return false;
  }
}

// Fallback list for runtimes without `Intl.supportedValuesOf` — notably Hermes
// on React Native, which ships a reduced Intl. Ordered roughly by expected use
// for this product's audience rather than alphabetically.
const FALLBACK_TIMEZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Phoenix',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu',
  'America/Toronto',
  'America/Vancouver',
  'America/Edmonton',
  'America/Winnipeg',
  'America/Halifax',
  'America/St_Johns',
  'America/Mexico_City',
  'America/Bogota',
  'America/Sao_Paulo',
  'America/Argentina/Buenos_Aires',
  'Europe/London',
  'Europe/Dublin',
  'Europe/Lisbon',
  'Europe/Madrid',
  'Europe/Paris',
  'Europe/Brussels',
  'Europe/Amsterdam',
  'Europe/Berlin',
  'Europe/Zurich',
  'Europe/Rome',
  'Europe/Stockholm',
  'Europe/Oslo',
  'Europe/Copenhagen',
  'Europe/Warsaw',
  'Europe/Prague',
  'Europe/Athens',
  'Europe/Helsinki',
  'Europe/Kyiv',
  'Europe/Istanbul',
  'Europe/Moscow',
  'Africa/Cairo',
  'Africa/Lagos',
  'Africa/Nairobi',
  'Africa/Johannesburg',
  'Asia/Jerusalem',
  'Asia/Dubai',
  'Asia/Karachi',
  'Asia/Kolkata',
  'Asia/Dhaka',
  'Asia/Bangkok',
  'Asia/Jakarta',
  'Asia/Singapore',
  'Asia/Hong_Kong',
  'Asia/Shanghai',
  'Asia/Manila',
  'Asia/Seoul',
  'Asia/Tokyo',
  'Australia/Perth',
  'Australia/Adelaide',
  'Australia/Brisbane',
  'Australia/Sydney',
  'Australia/Melbourne',
  'Australia/Hobart',
  'Pacific/Auckland',
  'Pacific/Fiji',
  'UTC',
];

/**
 * Every timezone the picker should offer.
 *
 * Uses the full IANA set when the runtime exposes it, otherwise the curated
 * fallback. `include` is appended so a family's currently-stored timezone and
 * the device's own zone always appear as options even if they're outside the
 * fallback list — otherwise a user in an unlisted zone would open the picker
 * and find their own setting missing.
 *
 * @param {string[]} [include] Extra timezones to guarantee are present
 * @returns {string[]} sorted, de-duplicated, validated
 */
export function listTimezones(include = []) {
  let base;
  try {
    base =
      typeof Intl.supportedValuesOf === 'function'
        ? Intl.supportedValuesOf('timeZone')
        : FALLBACK_TIMEZONES;
  } catch {
    base = FALLBACK_TIMEZONES;
  }

  const all = new Set(base);
  for (const tz of include) {
    if (tz && isValidTimezone(tz)) all.add(tz);
  }
  return Array.from(all).sort();
}

/**
 * Current UTC offset for a timezone, in minutes east of UTC.
 * Derived by formatting the same instant in the target zone and in UTC, so it
 * accounts for DST at the given moment instead of assuming a fixed offset.
 */
export function getTimezoneOffsetMinutes(tz, at = new Date()) {
  if (!isValidTimezone(tz)) return null;
  try {
    const dtf = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      hour12: false,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
    const parts = dtf.formatToParts(at).reduce((acc, p) => {
      acc[p.type] = p.value;
      return acc;
    }, {});
    const asUTC = Date.UTC(
      Number(parts.year),
      Number(parts.month) - 1,
      Number(parts.day),
      Number(parts.hour) % 24,
      Number(parts.minute),
      Number(parts.second)
    );
    return Math.round((asUTC - at.getTime()) / 60000);
  } catch {
    return null;
  }
}

/** 'America/New_York' -> 'UTC-04:00' */
export function formatUtcOffset(tz, at = new Date()) {
  const mins = getTimezoneOffsetMinutes(tz, at);
  if (mins === null) return '';
  const sign = mins < 0 ? '-' : '+';
  const abs = Math.abs(mins);
  const hh = String(Math.floor(abs / 60)).padStart(2, '0');
  const mm = String(abs % 60).padStart(2, '0');
  return `UTC${sign}${hh}:${mm}`;
}

/** 'America/New_York' -> 'New York, America' (human-facing label). */
export function formatTimezoneName(tz) {
  if (!tz) return '';
  const [region, ...rest] = tz.split('/');
  if (!rest.length) return tz.replace(/_/g, ' ');
  const city = rest.join(' / ').replace(/_/g, ' ');
  return `${city}, ${region.replace(/_/g, ' ')}`;
}

/**
 * Full picker label, e.g. 'New York, America — UTC-04:00 · 6:12 PM'.
 * Showing the local time is what makes the list scannable: people recognize
 * "the one that says 6pm" faster than they recognize an IANA identifier.
 */
export function formatTimezoneLabel(tz, at = new Date()) {
  const name = formatTimezoneName(tz);
  const offset = formatUtcOffset(tz, at);
  let time = '';
  try {
    time = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      hour: 'numeric',
      minute: '2-digit',
    }).format(at);
  } catch {
    time = '';
  }
  return [name, offset && `${offset}`, time].filter(Boolean).join(' · ');
}

/**
 * Resolve which timezone to use for a user, in precedence order:
 *   1. the user's own stamped timezone
 *   2. their family's configured timezone
 *   3. UTC
 */
export function resolveUserTimezone(userTimezone, familyTimezone) {
  if (isValidTimezone(userTimezone)) return userTimezone;
  if (isValidTimezone(familyTimezone)) return familyTimezone;
  return 'Etc/UTC';
}
